import React, { useState } from 'react';
import { FaGraduationCap, FaChartLine, FaBuilding, FaLaptopCode, FaCertificate, FaCalendarAlt, FaCheckCircle, FaRocket, FaBriefcase, FaUsers, FaBookOpen, FaClock, FaAward, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';

const CATrainingIndia = () => {
  const [activeCourse, setActiveCourse] = useState(null);
  const [activeWeek, setActiveWeek] = useState({});
  const [selectedProgram, setSelectedProgram] = useState('ca');

  const programs = {
    ca: {
      name: 'Chartered Accountant (CA)',
      duration: '1 Year',
      icon: <FaGraduationCap />,
      color: '#2563eb',
      eligibility: '+1, +2, Degree Students',
      description: 'Comprehensive CA training with practical experience in accounting, taxation, auditing, and financial management.'
    },
    icwa: {
      name: 'ICWA/CMA',
      duration: '9 Months',
      icon: <FaChartLine />,
      color: '#7c3aed',
      eligibility: '+1, +2, Degree Students',
      description: 'Cost and Management Accounting certification with focus on cost management, financial planning, and strategic management.'
    },
    cfa: {
      name: 'Chartered Financial Analyst (CFA)',
      duration: '6 Months',
      icon: <FaAward />,
      color: '#dc2626',
      eligibility: 'Degree Students',
      description: 'Investment management and financial analysis training covering portfolio management, equity investments, and risk analysis.'
    }
  };

  const courseDetails = {
    ca: {
      syllabus: [
        { module: 'Financial Accounting', topics: ['Journal Entries', 'Ledger Posting', 'Trial Balance', 'Financial Statements', 'Bank Reconciliation'] },
        { module: 'Cost Accounting', topics: ['Material Costing', 'Labor Costing', 'Overhead Allocation', 'Job Costing', 'Process Costing'] },
        { module: 'Taxation', topics: ['Income Tax', 'GST', 'TDS', 'Corporate Tax', 'International Taxation'] },
        { module: 'Auditing', topics: ['Internal Audit', 'Statutory Audit', 'Tax Audit', 'Audit Planning', 'Risk Assessment'] },
        { module: 'Corporate Laws', topics: ['Companies Act', 'SEBI Regulations', 'Corporate Governance', 'IPO Process', 'Compliance'] },
        { module: 'Financial Management', topics: ['Capital Budgeting', 'Working Capital', 'Risk Management', 'Financial Analysis', 'Valuation'] }
      ],
      weekPlan: [
        { week: 1, title: 'Foundation & Accounting Basics', topics: ['Introduction to Accounting', 'Double Entry System', 'Journal & Ledger', 'Practical Sessions'], practicals: 'Create 50+ journal entries, Manual ledger posting' },
        { week: 2, title: 'Trial Balance & Financial Statements', topics: ['Trial Balance Preparation', 'Trading Account', 'P&L Account', 'Balance Sheet'], practicals: 'Prepare complete financial statements for 5 companies' },
        { week: 3, title: 'Bank Reconciliation & Cash Book', topics: ['Cash Book Types', 'Bank Reconciliation', 'Petty Cash Book', 'Real-world Cases'], practicals: 'Reconcile 10 bank statements with accounting records' },
        { week: 4, title: 'Cost Accounting Fundamentals', topics: ['Material Costing', 'Labor Costing', 'Overhead Distribution', 'Cost Sheet'], practicals: 'Prepare cost sheets for manufacturing companies' },
        { week: 8, title: 'GST & Indirect Taxation', topics: ['GST Registration', 'Input Tax Credit', 'GST Returns', 'E-way Bills'], practicals: 'File GST returns on government portal' },
        { week: 12, title: 'Income Tax Filing', topics: ['ITR Forms', 'Tax Computation', 'Deductions', 'E-filing Process'], practicals: 'File 20+ income tax returns for individuals' },
        { week: 16, title: 'Tally ERP & Accounting Software', topics: ['Tally Prime', 'Company Creation', 'Voucher Entry', 'GST in Tally'], practicals: 'Complete accounting cycle in Tally for 3 companies' },
        { week: 20, title: 'Internal Audit Procedures', topics: ['Audit Planning', 'Internal Controls', 'Audit Testing', 'Documentation'], practicals: 'Conduct internal audit for startup company' },
        { week: 24, title: 'Statutory Audit', topics: ['Companies Act Audit', 'Audit Report', 'Sampling', 'Audit Evidence'], practicals: 'Assist in statutory audit of Pvt Ltd company' },
        { week: 28, title: 'Working Capital Management', topics: ['Cash Management', 'Inventory Management', 'Receivables', 'Payables'], practicals: 'Analyze working capital for 5 companies' },
        { week: 32, title: 'Financial Analysis & Ratios', topics: ['Liquidity Ratios', 'Profitability Ratios', 'Efficiency Ratios', 'Report Writing'], practicals: 'Prepare financial analysis report for Fortune 500 company' },
        { week: 36, title: 'Corporate Tax & Compliance', topics: ['Corporate Tax Planning', 'Transfer Pricing', 'Advance Tax', 'Tax Audit Report'], practicals: 'Prepare tax audit report under section 44AB' },
        { week: 40, title: 'MCA Compliance & ROC Filings', topics: ['Annual Returns', 'Financial Statements Filing', 'DIR-3 KYC', 'Board Resolutions'], practicals: 'File annual returns and financial statements on MCA portal' },
        { week: 44, title: 'SEBI & Capital Markets', topics: ['IPO Process', 'Stock Exchange Compliance', 'Insider Trading', 'Corporate Governance'], practicals: 'Study IPO prospectus of recent listings' },
        { week: 48, title: 'Final Project & Placement Prep', topics: ['Live Project', 'Interview Preparation', 'Resume Building', 'Mock Interviews'], practicals: 'Complete end-to-end audit and taxation project' }
      ],
      internships: [
        { phase: 'Months 1-3', company: 'Startup/MSME', role: 'Accounting Assistant', work: 'Day-to-day accounting, voucher entry, bank reconciliation, GST returns' },
        { phase: 'Months 4-6', company: 'Pvt Ltd Company', role: 'Tax Assistant', work: 'Income tax filing, TDS returns, tax planning, advance tax computation' },
        { phase: 'Months 7-9', company: 'CA Firm/Audit Firm', role: 'Audit Trainee', work: 'Internal audit, statutory audit, audit documentation, report preparation' },
        { phase: 'Months 10-12', company: 'Fortune 500/MNC', role: 'Finance Intern', work: 'Financial analysis, MIS reporting, budgeting, compliance management' }
      ]
    },
    icwa: {
      syllabus: [
        { module: 'Cost Management', topics: ['Cost Classification', 'Cost Sheet', 'Material Management', 'Labor Management', 'Overhead Analysis'] },
        { module: 'Financial Management', topics: ['Time Value of Money', 'Capital Budgeting', 'Leverage Analysis', 'Working Capital', 'Dividend Policy'] },
        { module: 'Management Accounting', topics: ['Budgetary Control', 'Standard Costing', 'Variance Analysis', 'Marginal Costing', 'CVP Analysis'] },
        { module: 'Strategic Management', topics: ['Strategic Planning', 'Performance Management', 'Balanced Scorecard', 'KPI Development', 'Business Strategy'] },
        { module: 'Operations Management', topics: ['Inventory Management', 'Supply Chain', 'Quality Control', 'Production Planning', 'JIT System'] }
      ],
      weekPlan: [
        { week: 1, title: 'Cost Accounting Basics', topics: ['Cost Classification', 'Cost Centers', 'Cost Allocation', 'Cost Sheet Preparation'], practicals: 'Prepare cost sheets for 10 products' },
        { week: 2, title: 'Material Costing', topics: ['EOQ Model', 'ABC Analysis', 'FIFO/LIFO', 'Material Waste', 'Pricing Methods'], practicals: 'Implement inventory management system' },
        { week: 4, title: 'Labor Costing & HR Metrics', topics: ['Time & Motion Study', 'Wage Systems', 'Labor Turnover', 'Productivity Analysis'], practicals: 'Analyze labor efficiency for manufacturing unit' },
        { week: 6, title: 'Overhead Costing', topics: ['Overhead Absorption', 'Activity Based Costing', 'Over/Under Absorption', 'Service Costing'], practicals: 'Allocate overheads for multi-product company' },
        { week: 8, title: 'Marginal Costing & CVP', topics: ['Contribution Margin', 'Break-even Analysis', 'P/V Ratio', 'Decision Making'], practicals: 'Perform CVP analysis for 5 business scenarios' },
        { week: 12, title: 'Standard Costing', topics: ['Standard Setting', 'Material Variance', 'Labor Variance', 'Overhead Variance'], practicals: 'Calculate and analyze variances for production unit' },
        { week: 16, title: 'Budgetary Control', topics: ['Master Budget', 'Functional Budgets', 'Cash Budget', 'Zero-based Budgeting'], practicals: 'Prepare complete budget for Pvt Ltd company' },
        { week: 20, title: 'Capital Budgeting', topics: ['NPV Method', 'IRR Method', 'Payback Period', 'Risk Analysis'], practicals: 'Evaluate 10 capital investment proposals' },
        { week: 24, title: 'Working Capital Management', topics: ['Operating Cycle', 'Cash Management Models', 'Receivables Management', 'Inventory Control'], practicals: 'Optimize working capital for trading company' },
        { week: 28, title: 'Strategic Cost Management', topics: ['Target Costing', 'Kaizen Costing', 'Life Cycle Costing', 'Value Chain Analysis'], practicals: 'Implement cost reduction strategies' },
        { week: 32, title: 'Performance Management', topics: ['KPI Dashboard', 'Balanced Scorecard', 'Benchmarking', 'Performance Evaluation'], practicals: 'Design KPI framework for startup' },
        { week: 36, title: 'Final Project & Certification', topics: ['Comprehensive Project', 'Case Studies', 'Interview Prep', 'Industry Certification'], practicals: 'Complete live cost management project' }
      ],
      internships: [
        { phase: 'Months 1-3', company: 'Manufacturing Startup', role: 'Cost Trainee', work: 'Cost sheet preparation, material management, variance analysis' },
        { phase: 'Months 4-6', company: 'Mid-size Company', role: 'Budget Analyst', work: 'Budget preparation, performance tracking, MIS reports' },
        { phase: 'Months 7-9', company: 'Fortune 500 Company', role: 'Management Analyst', work: 'Strategic costing, KPI development, financial planning' }
      ]
    },
    cfa: {
      syllabus: [
        { module: 'Quantitative Methods', topics: ['Time Value of Money', 'Statistical Analysis', 'Probability', 'Hypothesis Testing', 'Regression Analysis'] },
        { module: 'Economics', topics: ['Microeconomics', 'Macroeconomics', 'Currency Markets', 'Economic Indicators', 'International Trade'] },
        { module: 'Financial Statement Analysis', topics: ['Balance Sheet Analysis', 'Income Statement', 'Cash Flow Analysis', 'Financial Ratios', 'Quality of Earnings'] },
        { module: 'Equity Investments', topics: ['Equity Valuation', 'Industry Analysis', 'DCF Models', 'Relative Valuation', 'Stock Selection'] },
        { module: 'Fixed Income', topics: ['Bond Valuation', 'Duration & Convexity', 'Yield Curves', 'Credit Analysis', 'Bond Portfolio Management'] },
        { module: 'Portfolio Management', topics: ['Modern Portfolio Theory', 'CAPM', 'Asset Allocation', 'Risk Management', 'Performance Evaluation'] }
      ],
      weekPlan: [
        { week: 1, title: 'Investment Foundations', topics: ['Financial Markets Overview', 'Investment Vehicles', 'Market Participants', 'Regulatory Framework'], practicals: 'Analyze market structure and trading mechanisms' },
        { week: 2, title: 'Quantitative Methods', topics: ['TVM Calculations', 'Statistical Analysis', 'Probability Distributions', 'Excel Modeling'], practicals: 'Build financial models in Excel' },
        { week: 4, title: 'Financial Statement Analysis', topics: ['Balance Sheet Analysis', 'Income Statement', 'Cash Flow Statement', 'Ratio Analysis'], practicals: 'Analyze financial statements of 10 companies' },
        { week: 6, title: 'Equity Valuation - DCF', topics: ['Free Cash Flow', 'WACC Calculation', 'Terminal Value', 'Sensitivity Analysis'], practicals: 'Build DCF models for 5 listed companies' },
        { week: 8, title: 'Relative Valuation', topics: ['P/E Ratio', 'EV/EBITDA', 'Price to Book', 'Comparable Analysis'], practicals: 'Perform peer comparison for sector analysis' },
        { week: 10, title: 'Fixed Income Analysis', topics: ['Bond Pricing', 'Yield Calculations', 'Duration', 'Credit Risk'], practicals: 'Analyze bond portfolio and calculate returns' },
        { week: 12, title: 'Portfolio Theory', topics: ['Risk & Return', 'Diversification', 'Efficient Frontier', 'CAPM'], practicals: 'Build optimal portfolio using historical data' },
        { week: 14, title: 'Derivatives & Risk Management', topics: ['Options', 'Futures', 'Swaps', 'Hedging Strategies'], practicals: 'Design hedging strategies for portfolios' },
        { week: 16, title: 'Alternative Investments', topics: ['Private Equity', 'Hedge Funds', 'Real Estate', 'Commodities'], practicals: 'Evaluate alternative investment opportunities' },
        { week: 18, title: 'Ethics & Professional Standards', topics: ['CFA Code of Ethics', 'Professional Conduct', 'Case Studies', 'Compliance'], practicals: 'Analyze ethical dilemmas in investment management' },
        { week: 20, title: 'Portfolio Construction', topics: ['Asset Allocation', 'Security Selection', 'Rebalancing', 'Tax Efficiency'], practicals: 'Design investment strategy for different investors' },
        { week: 24, title: 'Capstone Project & Exam Prep', topics: ['Mock Exams', 'Stock Pitch Competition', 'Research Reports', 'Interview Prep'], practicals: 'Present comprehensive investment thesis' }
      ],
      internships: [
        { phase: 'Months 1-2', company: 'Fintech Startup', role: 'Research Analyst Intern', work: 'Company research, financial modeling, sector analysis' },
        { phase: 'Months 3-4', company: 'Investment Advisory Firm', role: 'Analyst Trainee', work: 'Portfolio analysis, client presentations, market research' },
        { phase: 'Months 5-6', company: 'Asset Management/Investment Bank', role: 'Investment Analyst', work: 'Equity research, valuation modeling, investment recommendations' }
      ]
    }
  };

  const features = [
    { icon: <FaBriefcase />, title: 'Real-time Experience', desc: 'Work with Startups, Fortune 500, and Private Companies', color: '#3b82f6' },
    { icon: <FaUsers />, title: 'Industry Mentors', desc: 'Learn from CA/CMA professionals with 10+ years experience', color: '#8b5cf6' },
    { icon: <FaLaptopCode />, title: 'Practical Training', desc: 'Hands-on experience with Tally, Excel, SAP, and more', color: '#ec4899' },
    { icon: <FaCertificate />, title: 'Certification', desc: 'Industry-recognized certification upon completion', color: '#f59e0b' }
  ];

  const toggleWeek = (courseKey, weekIndex) => {
    setActiveWeek(prev => ({
      ...prev,
      [courseKey]: prev[courseKey] === weekIndex ? null : weekIndex
    }));
  };

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', overflowX: 'hidden' }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '80px 20px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px', animation: 'fadeInDown 1s' }}>
            <FaGraduationCap style={{ marginRight: '15px' }} />
          </div>
          <h1 style={{ 
            fontSize: 'clamp(32px, 5vw, 56px)', 
            fontWeight: 'bold', 
            marginBottom: '20px',
            animation: 'fadeInDown 1s',
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
          }}>
            Professional Finance Training Academy
          </h1>
          <p style={{ 
            fontSize: 'clamp(18px, 3vw, 24px)', 
            marginBottom: '30px',
            animation: 'fadeInUp 1s',
            maxWidth: '800px',
            margin: '0 auto 30px'
          }}>
            Transform Your Career with CA, ICWA & CFA Training Programs
          </p>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            justifyContent: 'center', 
            gap: '15px',
            animation: 'fadeInUp 1.2s'
          }}>
            <div style={{ 
              background: 'rgba(255,255,255,0.2)', 
              padding: '15px 30px', 
              borderRadius: '30px',
              backdropFilter: 'blur(10px)'
            }}>
              <FaClock style={{ marginRight: '10px' }} />
              6-12 Months Programs
            </div>
            <div style={{ 
              background: 'rgba(255,255,255,0.2)', 
              padding: '15px 30px', 
              borderRadius: '30px',
              backdropFilter: 'blur(10px)'
            }}>
              <FaBriefcase style={{ marginRight: '10px' }} />
              100% Practical Training
            </div>
            <div style={{ 
              background: 'rgba(255,255,255,0.2)', 
              padding: '15px 30px', 
              borderRadius: '30px',
              backdropFilter: 'blur(10px)'
            }}>
              <FaAward style={{ marginRight: '10px' }} />
              Industry Certification
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container" style={{ padding: '60px 20px' }}>
        <h2 style={{ 
          textAlign: 'center', 
          marginBottom: '50px', 
          fontSize: 'clamp(28px, 4vw, 40px)',
          fontWeight: 'bold',
          color: '#1f2937'
        }}>
          Why Choose Our Training Programs?
        </h2>
        <div className="row g-4">
          {features.map((feature, index) => (
            <div key={index} className="col-12 col-sm-6 col-lg-3">
              <div style={{
                background: 'white',
                padding: '30px',
                borderRadius: '15px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                textAlign: 'center',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                cursor: 'pointer',
                height: '100%',
                border: `3px solid ${feature.color}15`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = `0 15px 40px ${feature.color}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
              }}>
                <div style={{ 
                  fontSize: '48px', 
                  color: feature.color, 
                  marginBottom: '20px',
                  animation: 'pulse 2s infinite'
                }}>
                  {feature.icon}
                </div>
                <h4 style={{ 
                  fontSize: '20px', 
                  fontWeight: 'bold', 
                  marginBottom: '15px',
                  color: '#1f2937'
                }}>
                  {feature.title}
                </h4>
                <p style={{ fontSize: '15px', color: '#6b7280', lineHeight: '1.6' }}>
                  {feature.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Programs Section */}
      <div style={{ background: '#f9fafb', padding: '60px 20px' }}>
        <div className="container">
          <h2 style={{ 
            textAlign: 'center', 
            marginBottom: '20px', 
            fontSize: 'clamp(28px, 4vw, 40px)',
            fontWeight: 'bold',
            color: '#1f2937'
          }}>
            Our Training Programs
          </h2>
          <p style={{ textAlign: 'center', marginBottom: '50px', fontSize: '18px', color: '#6b7280' }}>
            Choose the program that aligns with your career goals
          </p>
          
          <div className="row g-4 mb-5">
            {Object.entries(programs).map(([key, program]) => (
              <div key={key} className="col-12 col-md-6 col-lg-4">
                <div 
                  onClick={() => {
                    setSelectedProgram(key);
                    setActiveCourse(null);
                  }}
                  style={{
                    background: selectedProgram === key ? program.color : 'white',
                    color: selectedProgram === key ? 'white' : '#1f2937',
                    padding: '30px',
                    borderRadius: '15px',
                    boxShadow: selectedProgram === key ? `0 15px 40px ${program.color}60` : '0 10px 30px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    transform: selectedProgram === key ? 'scale(1.05)' : 'scale(1)',
                    border: `3px solid ${program.color}`
                  }}
                  onMouseEnter={(e) => {
                    if (selectedProgram !== key) {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = `0 15px 40px ${program.color}40`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedProgram !== key) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                    }
                  }}>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>
                    {program.icon}
                  </div>
                  <h3 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '15px' }}>
                    {program.name}
                  </h3>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '10px',
                    opacity: selectedProgram === key ? 1 : 0.8
                  }}>
                    <FaClock style={{ marginRight: '10px' }} />
                    <span style={{ fontSize: '16px', fontWeight: '600' }}>{program.duration}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '15px',
                    opacity: selectedProgram === key ? 1 : 0.8
                  }}>
                    <FaUsers style={{ marginRight: '10px' }} />
                    <span style={{ fontSize: '14px' }}>{program.eligibility}</span>
                  </div>
                  <p style={{ 
                    fontSize: '15px', 
                    lineHeight: '1.6',
                    opacity: selectedProgram === key ? 1 : 0.8
                  }}>
                    {program.description}
                  </p>
                  {selectedProgram === key && (
                    <div style={{ 
                      marginTop: '20px', 
                      padding: '10px 20px', 
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      textAlign: 'center'
                    }}>
                      ✓ Selected
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Course Details */}
          {selectedProgram && (
            <div style={{ 
              background: 'white', 
              padding: '40px', 
              borderRadius: '15px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
              animation: 'fadeIn 0.5s'
            }}>
              <div style={{ 
                borderBottom: `4px solid ${programs[selectedProgram].color}`,
                paddingBottom: '20px',
                marginBottom: '40px'
              }}>
                <h3 style={{ 
                  fontSize: 'clamp(24px, 4vw, 36px)', 
                  fontWeight: 'bold',
                  color: programs[selectedProgram].color,
                  marginBottom: '10px'
                }}>
                  {programs[selectedProgram].name} - Complete Curriculum
                </h3>
                <p style={{ fontSize: '18px', color: '#6b7280' }}>
                  Comprehensive training with practical experience and industry exposure
                </p>
              </div>

              {/* Syllabus */}
              <div className="mb-5">
                <h4 style={{ 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  marginBottom: '30px',
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <FaBookOpen style={{ marginRight: '15px', color: programs[selectedProgram].color }} />
                  Course Syllabus
                </h4>
                <div className="row g-4">
                  {courseDetails[selectedProgram].syllabus.map((module, index) => (
                    <div key={index} className="col-12 col-md-6">
                      <div style={{
                        background: `${programs[selectedProgram].color}10`,
                        padding: '25px',
                        borderRadius: '12px',
                        border: `2px solid ${programs[selectedProgram].color}30`,
                        height: '100%',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateX(5px)';
                        e.currentTarget.style.boxShadow = `0 5px 20px ${programs[selectedProgram].color}30`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                        <h5 style={{ 
                          fontSize: '20px', 
                          fontWeight: 'bold', 
                          marginBottom: '15px',
                          color: programs[selectedProgram].color
                        }}>
                          Module {index + 1}: {module.module}
                        </h5>
                        <ul style={{ 
                          listStyle: 'none', 
                          padding: 0,
                          margin: 0
                        }}>
                          {module.topics.map((topic, idx) => (
                            <li key={idx} style={{ 
                              marginBottom: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: '15px',
                              color: '#374151'
                            }}>
                              <FaCheckCircle style={{ 
                                color: programs[selectedProgram].color, 
                                marginRight: '10px',
                                flexShrink: 0
                              }} />
                              {topic}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Week-wise Roadmap */}
              <div className="mb-5">
                <h4 style={{ 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  marginBottom: '30px',
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <FaCalendarAlt style={{ marginRight: '15px', color: programs[selectedProgram].color }} />
                  Week-wise Training Roadmap
                </h4>
                <div style={{ position: 'relative' }}>
                  {/* Timeline Line */}
                  <div style={{
                    position: 'absolute',
                    left: '20px',
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    background: `linear-gradient(to bottom, ${programs[selectedProgram].color}, ${programs[selectedProgram].color}60)`,
                    display: window.innerWidth > 768 ? 'block' : 'none'
                  }} />
                  
                  {courseDetails[selectedProgram].weekPlan.map((week, index) => (
                    <div key={index} style={{ marginBottom: '20px', position: 'relative' }}>
                      <div
                        onClick={() => toggleWeek(selectedProgram, index)}
                        style={{
                          background: 'white',
                          padding: '20px',
                          paddingLeft: window.innerWidth > 768 ? '60px' : '20px',
                          borderRadius: '12px',
                          border: `2px solid ${programs[selectedProgram].color}30`,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: activeWeek[selectedProgram] === index ? `0 8px 25px ${programs[selectedProgram].color}30` : '0 2px 10px rgba(0,0,0,0.05)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = programs[selectedProgram].color;
                          e.currentTarget.style.transform = 'translateX(5px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = `${programs[selectedProgram].color}30`;
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}>
                        {/* Week Badge */}
                        {window.innerWidth > 768 && (
                          <div style={{
                            position: 'absolute',
                            left: '8px',
                            top: '20px',
                            width: '28px',
                            height: '28px',
                            background: programs[selectedProgram].color,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            boxShadow: `0 0 0 4px white, 0 0 0 6px ${programs[selectedProgram].color}`
                          }}>
                            {week.week}
                          </div>
                        )}
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: '14px', 
                              color: programs[selectedProgram].color,
                              fontWeight: 'bold',
                              marginBottom: '5px'
                            }}>
                              Week {week.week}
                            </div>
                            <h5 style={{ 
                              fontSize: '18px', 
                              fontWeight: 'bold',
                              color: '#1f2937',
                              marginBottom: '10px'
                            }}>
                              {week.title}
                            </h5>
                            <div style={{ 
                              fontSize: '14px', 
                              color: '#6b7280',
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '8px'
                            }}>
                              {week.topics.slice(0, 2).map((topic, idx) => (
                                <span key={idx} style={{
                                  background: `${programs[selectedProgram].color}15`,
                                  padding: '4px 12px',
                                  borderRadius: '20px',
                                  fontSize: '13px'
                                }}>
                                  {topic}
                                </span>
                              ))}
                              {week.topics.length > 2 && (
                                <span style={{
                                  background: `${programs[selectedProgram].color}15`,
                                  padding: '4px 12px',
                                  borderRadius: '20px',
                                  fontSize: '13px'
                                }}>
                                  +{week.topics.length - 2} more
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ 
                            fontSize: '24px', 
                            color: programs[selectedProgram].color,
                            marginLeft: '15px'
                          }}>
                            {activeWeek[selectedProgram] === index ? <FaChevronUp /> : <FaChevronDown />}
                          </div>
                        </div>

                        {/* Expanded Content */}
                        {activeWeek[selectedProgram] === index && (
                          <div style={{ 
                            marginTop: '20px', 
                            paddingTop: '20px', 
                            borderTop: `2px solid ${programs[selectedProgram].color}20`,
                            animation: 'fadeIn 0.3s'
                          }}>
                            <div className="row g-3">
                              <div className="col-12 col-md-6">
                                <h6 style={{ 
                                  fontWeight: 'bold', 
                                  marginBottom: '15px',
                                  color: programs[selectedProgram].color,
                                  fontSize: '16px'
                                }}>
                                  Topics Covered:
                                </h6>
                                <ul style={{ 
                                  listStyle: 'none', 
                                  padding: 0,
                                  margin: 0
                                }}>
                                  {week.topics.map((topic, idx) => (
                                    <li key={idx} style={{ 
                                      marginBottom: '8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      fontSize: '14px',
                                      color: '#374151'
                                    }}>
                                      <FaCheckCircle style={{ 
                                        color: programs[selectedProgram].color, 
                                        marginRight: '10px',
                                        fontSize: '12px',
                                        flexShrink: 0
                                      }} />
                                      {topic}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="col-12 col-md-6">
                                <h6 style={{ 
                                  fontWeight: 'bold', 
                                  marginBottom: '15px',
                                  color: programs[selectedProgram].color,
                                  fontSize: '16px'
                                }}>
                                  Practical Work:
                                </h6>
                                <div style={{
                                  background: `${programs[selectedProgram].color}10`,
                                  padding: '15px',
                                  borderRadius: '8px',
                                  border: `1px solid ${programs[selectedProgram].color}30`
                                }}>
                                  <p style={{ 
                                    margin: 0, 
                                    fontSize: '14px',
                                    color: '#374151',
                                    lineHeight: '1.6'
                                  }}>
                                    {week.practicals}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Internship Phases */}
              <div className="mb-5">
                <h4 style={{ 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  marginBottom: '30px',
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <FaRocket style={{ marginRight: '15px', color: programs[selectedProgram].color }} />
                  Internship & Real-time Experience
                </h4>
                <div className="row g-4">
                  {courseDetails[selectedProgram].internships.map((internship, index) => (
                    <div key={index} className="col-12 col-md-6 col-lg-3">
                      <div style={{
                        background: `linear-gradient(135deg, ${programs[selectedProgram].color}15 0%, ${programs[selectedProgram].color}05 100%)`,
                        padding: '25px',
                        borderRadius: '12px',
                        border: `2px solid ${programs[selectedProgram].color}30`,
                        height: '100%',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = `0 10px 30px ${programs[selectedProgram].color}30`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: '-20px',
                          right: '-20px',
                          fontSize: '80px',
                          color: `${programs[selectedProgram].color}10`,
                          fontWeight: 'bold'
                        }}>
                          {index + 1}
                        </div>
                        <div style={{ position: 'relative', zIndex: 1 }}>
                          <div style={{ 
                            fontSize: '14px', 
                            color: programs[selectedProgram].color,
                            fontWeight: 'bold',
                            marginBottom: '10px'
                          }}>
                            {internship.phase}
                          </div>
                          <div style={{ 
                            fontSize: '32px', 
                            color: programs[selectedProgram].color,
                            marginBottom: '15px'
                          }}>
                            <FaBuilding />
                          </div>
                          <h6 style={{ 
                            fontSize: '16px', 
                            fontWeight: 'bold',
                            color: '#1f2937',
                            marginBottom: '8px'
                          }}>
                            {internship.company}
                          </h6>
                          <div style={{
                            background: programs[selectedProgram].color,
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: 'bold',
                            display: 'inline-block',
                            marginBottom: '12px'
                          }}>
                            {internship.role}
                          </div>
                          <p style={{ 
                            fontSize: '14px',
                            color: '#6b7280',
                            margin: 0,
                            lineHeight: '1.6'
                          }}>
                            {internship.work}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <div style={{ textAlign: 'center', marginTop: '40px' }}>
                <button
                  style={{
                    background: `linear-gradient(135deg, ${programs[selectedProgram].color} 0%, ${programs[selectedProgram].color}cc 100%)`,
                    color: 'white',
                    padding: '18px 50px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    border: 'none',
                    borderRadius: '50px',
                    cursor: 'pointer',
                    boxShadow: `0 10px 30px ${programs[selectedProgram].color}40`,
                    transition: 'all 0.3s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-3px) scale(1.05)';
                    e.target.style.boxShadow = `0 15px 40px ${programs[selectedProgram].color}60`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0) scale(1)';
                    e.target.style.boxShadow = `0 10px 30px ${programs[selectedProgram].color}40`;
                  }}>
                  <FaRocket />
                  Enroll in {programs[selectedProgram].name}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

   

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes fadeInDown {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            padding: 0;
          }

          @media (max-width: 768px) {
            .container {
              padding-left: 15px;
              padding-right: 15px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default CATrainingIndia;